import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { tokens } from "../../Common/designTokens";
import { useAuth } from "../../Context/AuthProvider";
import MarketDataHealthModal from "./MarketDataHealthModal";
import {
  MA_WATCHLIST_PROCESSING_MESSAGE,
  USER_ROLES,
  isAdminRole,
} from "../../Constants/AppConstants";
import {
  getWatchlistStatusIndicatorTone,
  isWatchlistProcessing,
} from "../../Utils/maWatchlistStatusUtils";

const WATCHLIST_MENU_MAX_HEIGHT = 320;
const WATCHLIST_STATUS_DOT_SX = {
  width: 10,
  height: 10,
  borderRadius: "50%",
  flexShrink: 0,
};

const getWatchlistStatusDotColor = (watchlist) => {
  const tone = getWatchlistStatusIndicatorTone(watchlist);

  if (tone === "processing") {
    return "#f59e0b";
  }

  if (tone === "failed") {
    return "#ef4444";
  }

  return "";
};

const MAInventoryHeader = ({
  loading = false,
  totalElements = 0,
  pipelineHealth = {},
  marketDataHealth = {},
  customizeButtonRef,
  watchlists = [],
  selectedWatchlistId = "",
  activeWatchlistStatusMessage = "",
  activeWatchlistStatusSeverity = "info",
  onWatchlistChange,
  onWatchlistClick,
  onWatchlistEdit,
  onWatchlistDelete,
  onCreateWatchlistClick,
  onColumnsClick,
  onDownloadClick,
  onFilterClick,
  onClearFilters,
  hasActiveFilters = false,
  dailyProcessStatus = {},
  isDailyProcessBlocked = false,
}) => {
  const { role } = useAuth();
  const [watchlistSelectOpen, setWatchlistSelectOpen] = useState(false);
  const [adminHealthModalOpen, setAdminHealthModalOpen] = useState(false);

  const hasWatchlists = watchlists.length > 0;

  const selectedWatchlist = watchlists.find(
    (watchlist) =>
      String(watchlist?.watchlistId) === String(selectedWatchlistId),
  );
  const selectedWatchlistName = selectedWatchlist?.watchlistName || "";
  const statusTone = pipelineHealth?.tone || "warning";
  const statusLabel = pipelineHealth?.statusLabel || "Unknown";
  const statusMessage = pipelineHealth?.message || "Market data health is unavailable.";
  const dailyProcessCode = String(dailyProcessStatus?.status || "").trim().toUpperCase();
  const isAdmin = isAdminRole(role);
  const hasAdminDailyProcessAlert =
    isAdmin
    && (dailyProcessStatus?.showAdminAlert === true || dailyProcessCode === "FAILED");

  const toneStyles = {
    success: {
      dot: "#22c55e",
      chipBg: "rgba(34, 197, 94, 0.12)",
      chipBorder: "rgba(34, 197, 94, 0.35)",
      text: "#15803d",
    },
    warning: {
      dot: "#f59e0b",
      chipBg: "rgba(245, 158, 11, 0.12)",
      chipBorder: "rgba(245, 158, 11, 0.35)",
      text: "#b45309",
    },
    error: {
      dot: "#ef4444",
      chipBg: "rgba(239, 68, 68, 0.12)",
      chipBorder: "rgba(239, 68, 68, 0.35)",
      text: "#b91c1c",
    },
  };
  const activeTone = toneStyles[statusTone] || toneStyles.warning;

  return (
    <Box
      sx={{
        color: tokens.colors.fontColour,
        padding: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontSize: { xs: "1rem", sm: "1.25rem" },
              fontWeight: 700,
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
            }}
          >
            Watchlist
            {loading ? (
              <CircularProgress
                sx={{ ml: 0.8, color: tokens.palette.primary }}
                size={20}
              />
            ) : (
              ` (${totalElements.toLocaleString()})`
            )}
          </Typography>
          {isDailyProcessBlocked ? (
            <Chip
              icon={<AutorenewOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
              label="Daily watchlist update in progress..."
              sx={{
                fontWeight: 700,
                color: "#b45309",
                backgroundColor: "rgba(245, 158, 11, 0.12)",
                border: "1px solid rgba(245, 158, 11, 0.35)",
                "& .MuiChip-icon": {
                  color: "#b45309",
                },
              }}
            />
          ) : null}
          {hasAdminDailyProcessAlert ? (
            <Chip
              icon={<ErrorOutlineOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
              label="Daily Process Failed"
              sx={{
                fontWeight: 700,
                color: "#b91c1c",
                backgroundColor: "rgba(239, 68, 68, 0.12)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                "& .MuiChip-icon": {
                  color: "#b91c1c",
                },
              }}
            />
          ) : null}

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: "999px",
              border: `1px solid ${activeTone.chipBorder}`,
              backgroundColor: activeTone.chipBg,
              width: "fit-content",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: activeTone.dot,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 700,
                color: activeTone.text,
              }}
            >
              {statusLabel}
            </Typography>
            {statusTone === "error" || statusTone === "warning" ? (
              <Tooltip title={statusMessage} placement="right">
                <InfoOutlinedIcon
                  sx={{
                    fontSize: "1rem",
                    color: activeTone.text,
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
          {isAdmin && (
            <Tooltip title="View Market Data And Daily Process Health" placement="right">
              <IconButton
                size="small"
                onClick={() => setAdminHealthModalOpen(true)}
                sx={{
                  ml: 1,
                  color: hasAdminDailyProcessAlert ? tokens.palette.danger : tokens.palette.primary,
                  "&:hover": {
                    backgroundColor: hasAdminDailyProcessAlert
                      ? "rgba(211, 47, 47, 0.08)"
                      : tokens.palette.primaryA10,
                  },
                }}
              >
                <AdminPanelSettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <MarketDataHealthModal
        open={adminHealthModalOpen}
        onClose={() => setAdminHealthModalOpen(false)}
        marketDataHealth={marketDataHealth}
        dailyProcessStatus={dailyProcessStatus}
      />

      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}
      >
        {activeWatchlistStatusMessage ? (
          <Alert
            severity={activeWatchlistStatusSeverity}
            sx={{
              py: 0,
              px: 1,
              height: "32px",
              alignItems: "center",
              minWidth: 0,
              "& .MuiAlert-message": {
                padding: 0,
                fontSize: "0.82rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              },
              "& .MuiAlert-icon": {
                padding: 0,
                marginRight: 1,
                display: "none",
              },
            }}
          >
            <span>
              {activeWatchlistStatusSeverity === "error"
                ? "Failed"
                : activeWatchlistStatusSeverity === "warning"
                  ? "Watchlist Operation Warning"
                  : "Processing..."}
            </span>
            <Tooltip title={activeWatchlistStatusMessage} placement="bottom">
              <InfoOutlinedIcon
                sx={{ fontSize: "1rem", cursor: "pointer", opacity: 0.8 }}
              />
            </Tooltip>
          </Alert>
        ) : null}
        <FormControl sx={{ width: 230 }}>
          <Select
            disabled={isDailyProcessBlocked}
            open={watchlistSelectOpen}
            onOpen={() => setWatchlistSelectOpen(true)}
            onClose={() => setWatchlistSelectOpen(false)}
            value={selectedWatchlistId ?? ""}
            displayEmpty
            onChange={(event) => {
              const nextValue = event.target.value;
              if (nextValue) {
                onWatchlistChange?.(nextValue);
              }
            }}
            renderValue={() => {
              if (!selectedWatchlistName) {
                return "Watchlists";
              }

              const selectedWatchlistDotColor =
                getWatchlistStatusDotColor(selectedWatchlist);

              return (
                <Box
                  title={selectedWatchlistName}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  {selectedWatchlistDotColor ? (
                    <Box
                      component="span"
                      sx={{
                        ...WATCHLIST_STATUS_DOT_SX,
                        backgroundColor: selectedWatchlistDotColor,
                      }}
                    />
                  ) : null}
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {selectedWatchlistName}
                  </Box>
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: 230,
                  maxHeight: WATCHLIST_MENU_MAX_HEIGHT,
                  overflow: "hidden",
                },
              },
              MenuListProps: {
                sx: {
                  py: 0,
                  maxHeight: WATCHLIST_MENU_MAX_HEIGHT,
                  overflowY: "auto",
                },
              },
            }}
            sx={{
              height: 42,
              width: "100%",
              backgroundColor: tokens.colors.defaultGridRowColor,
              color: tokens.colors.fontColour,
              borderRadius: "8px",
              "& .MuiSelect-select": {
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                pl: 0.5,
                pr: 4,
              },
            }}
          >
            {!hasWatchlists ? (
              <MenuItem disabled value="__no_watchlists__">
                No watchlists available
              </MenuItem>
            ) : (
              watchlists.map((watchlist, index) => {
                const processing = isWatchlistProcessing(watchlist);
                const watchlistDotColor = getWatchlistStatusDotColor(watchlist);

                return (
                  <MenuItem
                    key={watchlist.watchlistId}
                    value={watchlist.watchlistId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      borderBottom:
                        index < watchlists.length - 1
                          ? `1px solid ${tokens.colors.bodyBorderColor50}`
                          : "none",
                    }}
                  >
                    <Box
                      title={watchlist.watchlistName}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        width: "100%",
                      }}
                    >
                      {watchlistDotColor ? (
                        <Box
                          component="span"
                          sx={{
                            ...WATCHLIST_STATUS_DOT_SX,
                            backgroundColor: watchlistDotColor,
                          }}
                        />
                      ) : null}
                      <Box
                        component="span"
                        sx={{
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {watchlist.watchlistName}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip
                        title={
                          processing
                            ? MA_WATCHLIST_PROCESSING_MESSAGE
                            : "Edit watchlist"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={processing}
                            onMouseDown={(event) => {
                              event.preventDefault();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setWatchlistSelectOpen(false);
                              onWatchlistEdit?.(watchlist);
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip
                        title={
                          processing
                            ? MA_WATCHLIST_PROCESSING_MESSAGE
                            : "Delete watchlist"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={processing}
                            onMouseDown={(event) => {
                              event.preventDefault();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setWatchlistSelectOpen(false);
                              onWatchlistDelete?.(watchlist);
                            }}
                            sx={{ color: tokens.palette.danger }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </MenuItem>
                );
              })
            )}

            <MenuItem
              value="__create_watchlist__"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setWatchlistSelectOpen(false);
                onCreateWatchlistClick?.();
              }}
              sx={{
                display: "flex",
                gap: 1,
                fontWeight: 700,
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
                backgroundColor: tokens.colors.defaultGridRowColor,
                "&:hover": {
                  backgroundColor: tokens.colors.defaultGridHoverColor,
                },
              }}
            >
              <AddIcon fontSize="small" />
              Create Watchlist
            </MenuItem>
          </Select>
        </FormControl>


        <Button
          variant="contained"
          color="inherit"
          startIcon={<TuneIcon />}
          onClick={onWatchlistClick}
          disabled={isDailyProcessBlocked}
          sx={{
            height: 42,
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          Manage Watchlists
        </Button>

        <Tooltip
          title={
            !totalElements
              ? "No quotes to filter"
              : hasActiveFilters
                ? "Filters applied — Filter Quotes"
                : "Filter Quotes"
          }
        >
          <span>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<FilterAltOutlinedIcon />}
              onClick={onFilterClick}
              // Nothing to filter when the selected watchlist has no quotes.
              disabled={isDailyProcessBlocked || !totalElements}
              // When filters are active, a trailing ✕ clears them in place. Its
              // click is stopped from bubbling so it never opens the modal.
              endIcon={
                hasActiveFilters ? (
                  <Box
                    component="span"
                    role="button"
                    aria-label="Clear filters"
                    title="Clear filters"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onClearFilters?.();
                    }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      color: tokens.palette.danger,
                      "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "1.25rem" }} />
                  </Box>
                ) : undefined
              }
              sx={{
                height: 42,
                textTransform: "none",
                borderRadius: "8px",
                // Disabled: grey the text/icon too (the theme's containedInherit
                // colour otherwise keeps the label looking enabled). The extra
                // class out-specifies both the theme and the active override.
                "&.MuiButton-containedInherit.Mui-disabled": {
                  color: tokens.colors.disabledFontColour,
                },
                // Inactive: no colour override, so the shared theme style applies
                // and this button matches Manage Watchlists / Columns exactly.
                // Active: scope under `&.MuiButton-containedInherit` so it matches
                // the theme override's specificity and wins by insertion order — a
                // plain single-class sx loses to it (that's why the highlight
                // didn't show before). A low-opacity primary tint over the base
                // colour with a full primary outline (no badge/dot).
                ...(hasActiveFilters
                  ? {
                      "&.MuiButton-containedInherit": {
                        color: tokens.palette.primary,
                        backgroundColor: tokens.palette.primaryA10,
                        border: `1px solid ${tokens.palette.primary}`,
                        "&:hover": {
                          backgroundColor: tokens.palette.primaryA10,
                        },
                      },
                    }
                  : {}),
              }}
            >
              Quotes
            </Button>
          </span>
        </Tooltip>
    
        <Button
          ref={customizeButtonRef}
          variant="contained"
          color="inherit"
          startIcon={<ViewColumnOutlinedIcon />}
          onClick={onColumnsClick}
          disabled={isDailyProcessBlocked}
          sx={{
            height: 42,
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          Columns
        </Button>

        <DownloadMenu onSelectFormat={onDownloadClick} disabled={isDailyProcessBlocked} />
      </Box>
    </Box>
  );
};

const DownloadMenu = ({ onSelectFormat, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="Download">
        <IconButton
          disabled={disabled}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            borderRadius: "8px",
            color: tokens.colors.fontColour,
            width: 42,
            height: 42,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          <DownloadIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={close}>
        <MenuItem
          onClick={() => {
            close();
            onSelectFormat("csv");
          }}
        >
          Download CSV
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            onSelectFormat("xlsx");
          }}
        >
          Download Excel
        </MenuItem>
      </Menu>
    </>
  );
};

export default MAInventoryHeader;
